import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import { useCmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListResource";
import { findCloudRowForEnvironment } from "./cloudRouteCatalogRow";
import { useSavedEnvironmentRegistryStore } from "../environments/runtime/catalog";
import { EnvironmentId, type ScopedThreadRef } from "@cmmd-forge/contracts";
import { useCmmdRuntimeInventorySnapshot } from "../cmmd/cmmdRuntimeInventoryResource";
import { getStoredLocalDeviceRuntimeId } from "../deviceRegistryIdentity";
import { findRemoteRuntimeThreadRouteTarget } from "../components/chat/remoteRuntimeThreadRoute.logic";

/**
 * Which Environment id a legacy-shaped thread route should treat as
 * canonical.
 *
 * The saved-environment registry only gets a durable id once a person
 * "connects" a project from the sidebar (`environments/runtime/catalog.ts`'s
 * own docs: `byId` starts empty until then). A cold deep link, whether
 * CMMD's embed or a bookmarked `/cmmd-runtime:{runtimeId}/{threadId}` URL,
 * never does that, so the registry has nothing to offer on a fresh session
 * even though the runtime is live and provisioned (CMMD#5882). The cloud project
 * catalog already carries the durable id for that same row, reported
 * straight from CMMD, so it is checked first; the registry stays a fallback
 * for the runtime-inventory-only path (no catalog row) the second existing
 * test below covers.
 */
function resolveCanonicalEnvironmentId(input: {
  readonly routeEnvironmentId: string;
  readonly remoteTargetRuntimeId: string | null;
  readonly cloudRowCmmdEnvironmentId: string | null;
  readonly registryEnvironmentIds: readonly {
    readonly runtimeId?: string;
    readonly environmentId: EnvironmentId;
  }[];
}): EnvironmentId | null {
  if (input.remoteTargetRuntimeId === null) return null;
  if (input.cloudRowCmmdEnvironmentId === input.routeEnvironmentId) return null;
  if (
    input.cloudRowCmmdEnvironmentId !== null &&
    // A null legacyRuntimeId is not enough: `local` and
    // `cloud-project-placeholder` also report it, and either would scope
    // canonicalThreadRef to a non-durable id. Only `durable-environment`
    // survives Sprite replacement, which is the whole point of preferring
    // it here.
    classifyEnvironmentId(input.cloudRowCmmdEnvironmentId).shape === "durable-environment"
  ) {
    return EnvironmentId.make(input.cloudRowCmmdEnvironmentId);
  }
  return (
    input.registryEnvironmentIds.find(
      (record) =>
        record.runtimeId === input.remoteTargetRuntimeId &&
        record.environmentId !== input.routeEnvironmentId &&
        // Excludes the volatile `cmmd-runtime:` shape specifically, not
        // every cloud shape: a durable Environment id is exactly the stable
        // replacement this lookup exists to surface, so it must stay
        // eligible here.
        classifyEnvironmentId(record.environmentId).legacyRuntimeId === null,
    )?.environmentId ?? null
  );
}

export function useRemoteThreadRouteIdentity(routeThreadRef: ScopedThreadRef | null) {
  const environmentId = routeThreadRef?.environmentId ?? null;
  const threadId = routeThreadRef?.threadId ?? null;
  const runtimeInventory = useCmmdRuntimeInventorySnapshot();
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const savedRuntimeId = useSavedEnvironmentRegistryStore((state) =>
    routeThreadRef ? state.byId[routeThreadRef.environmentId]?.runtimeId : null,
  );
  const cloudRow = findCloudRowForEnvironment(environmentId, cloudCatalog.rows, []);
  const savedCloudRuntime = runtimeInventory.inventory?.runtimes.find(
    (candidate) =>
      candidate.runtime.id === savedRuntimeId && candidate.runtime.runtimeKind === "cloud-vm",
  );
  const remoteTarget = findRemoteRuntimeThreadRouteTarget({
    cloudRuntimeId: cloudRow?.runtimeId ?? savedCloudRuntime?.runtime.id ?? null,
    environmentId: environmentId ?? "",
    threadId: threadId ?? "",
    currentRegisteredRuntimeId: getStoredLocalDeviceRuntimeId(),
    runtimes: runtimeInventory.inventory?.runtimes ?? [],
    inventoryResolved:
      runtimeInventory.inventory !== null &&
      !runtimeInventory.isLoading &&
      runtimeInventory.error === null,
  });
  const canonicalEnvironmentId = useSavedEnvironmentRegistryStore((state) =>
    routeThreadRef === null
      ? null
      : resolveCanonicalEnvironmentId({
          routeEnvironmentId: environmentId ?? "",
          remoteTargetRuntimeId: remoteTarget?.runtimeId ?? null,
          cloudRowCmmdEnvironmentId: cloudRow?.row.cmmdEnvironmentId ?? null,
          registryEnvironmentIds: Object.values(state.byId),
        }),
  );
  return { remoteTarget, canonicalEnvironmentId };
}
